import * as api from "@/common/api";

export interface KeywordSearchRequest {
  seed: string;
  minVolume?: number | null;
  maxVolume?: number | null;
  limit?: number | null;
}

export interface KeywordRow {
  keyword: string;
  pcVolume: number;
  mobileVolume: number;
  totalVolume: number;
  volumeMasked: boolean;
  growth: number | null;
  competition: string | null;
  docCount: number | null;
  saturation: number | null;
  /** 씨앗과의 연관도. direct=씨앗을 그대로 품음, partial=낱말 일부만, expanded=광고 타겟팅으로만 엮임 */
  relevance: "direct" | "partial" | "expanded" | null;
}

export interface KeywordSearchResult {
  seed: string;
  totalRelated: number;
  filtered: number;
  analyzed: number;
  directCount: number;
  partialCount: number;
  expandedCount: number;
  rows: KeywordRow[];
  docCountAvailable: boolean;
  docCountFailed: number;
}

export interface KeywordProfile {
  keyword: string;
  pcVolume: number;
  mobileVolume: number;
  totalVolume: number;
  mobileShare: number | null;
  docCount: number | null;
  saturation: number | null;
  competition: string | null;
  totalClicks: number | null;
  mobileCtr: number | null;
  adDepth: number | null;
  firstPositionBid: number | null;
  trend: { period: string; ratio: number }[];
  datalabAvailable: boolean;
  datalabMessage: string | null;
  notice: string | null;
  topBlogs: {
    title: string;
    link: string;
    description: string;
    blogger: string;
    postDate: string;
  }[] | null;
}

export interface KeywordInsight {
  keyword: string;
  available: boolean;
  message: string | null;
  weekday: { label: string; ratio: number; peak: boolean }[] | null;
  gender: { label: string; points: { period: string; ratio: number }[] }[] | null;
  age: { label: string; points: { period: string; ratio: number }[] }[] | null;
}

export interface KeywordDetail {
  keyword: string;
  pcVolume: number;
  mobileVolume: number;
  totalVolume: number;
  competition: string | null;
  docCount: number | null;
  saturation: number | null;
  trend: { period: string; ratio: number }[];
}

/**
 * 씨앗 키워드 확장 조회. 응답 envelope 전체를 돌려준다(에러 메시지를 화면에서 써야 하므로).
 *
 * ⚠️ 타임아웃을 따로 준다. 문서 수를 순차 조회(네이버 초당 제한 회피)하므로
 * 40개 기준 12~15초가 걸린다. 공통 기본값 10초로는 항상 끊긴다.
 */
const SEARCH_TIMEOUT_MS = 60000;

export const search = async (payload: KeywordSearchRequest) => {
  return await api.post(
    "/api/v1/keyword/search",
    payload,
    new api.HeaderOption(),
    SEARCH_TIMEOUT_MS
  );
};

/** 키워드 프로필 — 연관 키워드보다 빨라서 먼저 렌더한다. */
export const getProfile = async (keyword: string) => {
  return await api.get(
    "/api/v1/keyword/profile",
    { keyword },
    new api.HeaderOption(),
    true,
    SEARCH_TIMEOUT_MS
  );
};

/**
 * 기간 단위별 검색 추이. unit = date | week | month.
 * ⚠️ 데이터랩은 이 셋만 지원한다 — 시간 단위는 제공되지 않는다.
 */
export const getTrend = async (keyword: string, unit: "date" | "week" | "month") => {
  return await api.get(
    "/api/v1/keyword/trend",
    { keyword, unit },
    new api.HeaderOption(),
    true,
    SEARCH_TIMEOUT_MS
  );
};

/**
 * 분야별 인기 키워드 — 씨앗 주제의 연관 키워드를 검색량순으로.
 * 발행량을 조회하지 않아 1~2초로 빠르다.
 */
export const getPopular = async (seed: string, limit = 50, rising = false) => {
  return await api.get(
    "/api/v1/keyword/popular",
    { seed, limit, rising },
    new api.HeaderOption(),
    true,
    SEARCH_TIMEOUT_MS
  );
};

export interface RealtimeTrend {
  source: string;
  fetchedAt: string;
  naverVolumeAvailable: boolean;
  categories: { code: string; label: string; count: number }[];
  items: {
    rank: number;
    keyword: string;
    category: string;
    categoryLabel: string;
    approxTraffic: string | null;
    naverVolume: number | null;
    newsTitle: string | null;
    newsUrl: string | null;
    newsSource: string | null;
  }[];
}

/** 구글 실시간 트렌드 (구글 RSS 30분 캐시 + 우리 분류 + 네이버 검색량) */
export const getRealtimeTrends = async () => {
  return await api.get(
    "/api/v1/keyword/trends/realtime",
    {},
    new api.HeaderOption(),
    true,
    SEARCH_TIMEOUT_MS
  );
};

/** 제목 진단 + 제목 추천 (규칙 기반) */
export const analyzeTitle = async (title: string) => {
  return await api.get(
    "/api/v1/keyword/title",
    { title },
    new api.HeaderOption(),
    true,
    SEARCH_TIMEOUT_MS
  );
};

/** 성향·요일 분석 — 데이터랩 호출이 8회라 프로필과 따로 부른다. */
export const getInsight = async (keyword: string) => {
  return await api.get(
    "/api/v1/keyword/insight",
    { keyword },
    new api.HeaderOption(),
    true,
    SEARCH_TIMEOUT_MS
  );
};

export const getDetail = async (keyword: string) => {
  return await api.get(
    "/api/v1/keyword/detail",
    { keyword },
    new api.HeaderOption(),
    true,
    SEARCH_TIMEOUT_MS
  );
};
